import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import SiteProofLogo from '../../components/SiteProofLogo';
import Button from '../../components/ui/Button';
import { authService } from '../../services/authService';
import { useLanguage } from '../../contexts/LanguageContext';

const STORAGE_KEY = 'verification_pending_email';

const EmailVerificationPending = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Persist email to sessionStorage so it survives a page refresh.
  // Priority: location.state.email (fresh navigation) → sessionStorage (refresh)
  const [email, setEmail] = useState(() => {
    const stateEmail = location?.state?.email || '';
    if (stateEmail) {
      try { sessionStorage.setItem(STORAGE_KEY, stateEmail); } catch (_) {}
      return stateEmail;
    }
    try { return sessionStorage.getItem(STORAGE_KEY) || ''; } catch (_) { return ''; }
  });

  const [resendStatus, setResendStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [resendError, setResendError] = useState('');

  const handleResend = async () => {
    if (!email) {
      setResendStatus('error');
      setResendError('No email address found. Please go back and sign up again.');
      return;
    }
    setResendStatus('sending');
    setResendError('');

    console.log('[EmailVerificationPending] Resending verification email to:', email);

    const { error } = await authService?.resendVerificationEmail(email);

    if (error) {
      console.error('[EmailVerificationPending] Resend error:', { status: error?.status, code: error?.code, message: error?.message });
      const msg = error?.message || '';
      let friendlyMsg = 'Failed to resend verification email. Please try again.';
      // HTTP-status branch is the canonical signal (Supabase auth returns
      // .status on AuthApiError); the message-substring branch stays as a
      // fallback for older error shapes that don't carry .status.
      if (error?.status === 429 ||
          msg?.toLowerCase()?.includes('rate limit') ||
          msg?.toLowerCase()?.includes('too many')) {
        friendlyMsg = 'Too many emails sent recently. Please wait a few minutes before trying again.';
      } else if (msg?.toLowerCase()?.includes('already confirmed') || msg?.toLowerCase()?.includes('already registered')) {
        friendlyMsg = 'This email is already verified. Please sign in.';
      } else if (msg) {
        friendlyMsg = msg;
      }
      setResendStatus('error');
      setResendError(friendlyMsg);
    } else {
      console.log('[EmailVerificationPending] Resend successful');
      setResendStatus('sent');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-lg shadow-elevation-3 p-6 md:p-8 border border-border">
            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
              <SiteProofLogo size="lg" />
            </div>

            {/* Email icon */}
            <div className="flex justify-center mb-6">
              <div className="flex items-center justify-center w-20 h-20 bg-accent/10 rounded-full">
                <Icon name="Mail" size={40} color="var(--color-accent)" />
              </div>
            </div>

            {/* Heading */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-foreground font-heading mb-3">
                {t('verify.heading')}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('verify.desc')}{' '}
                {email ? (
                  <span className="font-medium text-foreground">{email}</span>
                ) : (
                  t('verify.desc_no_email')
                )}
                {t('verify.desc2')}
              </p>
            </div>

            {/* Spam notice */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
              <Icon name="AlertTriangle" size={18} color="#d97706" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 leading-relaxed">
                {t('verify.spam_notice')} <strong>{t('verify.spam_bold1')}</strong> {t('verify.spam_notice2') === t('verify.spam_notice2') ? 'or' : ''} <strong>{t('verify.spam_bold2')}</strong> {t('verify.spam_notice2')}
              </p>
            </div>

            {/* Resend feedback */}
            {resendStatus === 'sent' && (
              <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                <Icon name="CheckCircle2" size={18} color="#16a34a" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">
                  {t('verify.resent_success')}
                </p>
              </div>
            )}
            {resendStatus === 'error' && (
              <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg mb-4">
                <Icon name="AlertCircle" size={18} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{resendError}</p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                fullWidth
                loading={resendStatus === 'sending'}
                disabled={resendStatus === 'sending'}
                onClick={handleResend}
                iconName="RefreshCw"
                iconPosition="left"
              >
                {resendStatus === 'sending' ? t('verify.resend_sending') : t('verify.resend_btn')}
              </Button>

              <Button
                type="button"
                variant="default"
                fullWidth
                onClick={() => navigate('/login')}
                iconName="LogIn"
                iconPosition="left"
              >
                {t('verify.back_to_signin')}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground mt-6 leading-relaxed">
              {t('verify.footer_note')}
            </p>
          </div>
        </div>
      </div>
      <footer className="py-6 px-4 border-t border-border bg-card">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            {t('verify.copyright')?.replace('{year}', new Date()?.getFullYear())}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EmailVerificationPending;
